import React, { useState, useEffect } from 'react';
import { MapContainer, TileLayer, GeoJSON, Popup, ZoomControl, Circle, CircleMarker, Marker } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import { getRegionCoordinates } from '../../data/coordinates';

// Fix for default markers in react-leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

// France wine regions GeoJSON data
const franceWineRegions = {
  type: "FeatureCollection",
  features: [
    {
      type: "Feature",
      properties: {
        name: "Bordeaux",
        region: "Bordeaux",
        description: "Région viticole renommée pour ses vins rouges",
        color: "#8B0000",
        type: "region"
      },
      geometry: {
        type: "Polygon",
        coordinates: [[
          [-0.8, 44.8], [-0.4, 44.8], [-0.4, 45.2], [-0.8, 45.2], [-0.8, 44.8]
        ]]
      }
    },
    {
      type: "Feature",
      properties: {
        name: "Bourgogne",
        region: "Bourgogne",
        description: "Région viticole célèbre pour ses vins de terroir",
        color: "#4B0082",
        type: "region"
      },
      geometry: {
        type: "Polygon",
        coordinates: [[
          [4.5, 46.8], [5.2, 46.8], [5.2, 47.5], [4.5, 47.5], [4.5, 46.8]
        ]]
      }
    },
    {
      type: "Feature",
      properties: {
        name: "Champagne",
        region: "Champagne",
        description: "Région viticole célèbre pour ses vins effervescents",
        color: "#FFD700",
        type: "region"
      },
      geometry: {
        type: "Polygon",
        coordinates: [[
          [3.8, 48.8], [4.5, 48.8], [4.5, 49.3], [3.8, 49.3], [3.8, 48.8]
        ]]
      }
    },
    {
      type: "Feature",
      properties: {
        name: "Provence",
        region: "Provence",
        description: "Région viticole méditerranéenne",
        color: "#FF6B6B",
        type: "region"
      },
      geometry: {
        type: "Polygon",
        coordinates: [[
          [5.2, 43.2], [6.2, 43.2], [6.2, 44.0], [5.2, 44.0], [5.2, 43.2]
        ]]
      }
    },
    {
      type: "Feature",
      properties: {
        name: "Rhône",
        region: "Rhône",
        description: "Région viticole de la vallée du Rhône",
        color: "#8B4513",
        type: "region"
      },
      geometry: {
        type: "Polygon",
        coordinates: [[
          [4.5, 44.0], [5.2, 44.0], [5.2, 45.0], [4.5, 45.0], [4.5, 44.0]
        ]]
      }
    },
    {
      type: "Feature",
      properties: {
        name: "Loire",
        region: "Loire",
        description: "Région viticole de la vallée de la Loire",
        color: "#32CD32",
        type: "region"
      },
      geometry: {
        type: "Polygon",
        coordinates: [[
          [-1.5, 47.0], [-0.5, 47.0], [-0.5, 47.8], [-1.5, 47.8], [-1.5, 47.0]
        ]]
      }
    },
    {
      type: "Feature",
      properties: {
        name: "Alsace",
        region: "Alsace",
        description: "Région viticole alsacienne",
        color: "#FF69B4",
        type: "region"
      },
      geometry: {
        type: "Polygon",
        coordinates: [[
          [7.2, 47.8], [8.0, 47.8], [8.0, 48.5], [7.2, 48.5], [7.2, 47.8]
        ]]
      }
    },
    {
      type: "Feature",
      properties: {
        name: "Languedoc-Roussillon",
        region: "Languedoc-Roussillon",
        description: "Région viticole du sud de la France",
        color: "#FF4500",
        type: "region"
      },
      geometry: {
        type: "Polygon",
        coordinates: [[
          [2.5, 42.5], [3.5, 42.5], [3.5, 43.5], [2.5, 43.5], [2.5, 42.5]
        ]]
      }
    }
  ]
};

// Provence AOCs with clean design
const provenceAOCs = [
  {
    name: "Bandol",
    region: "Provence",
    description: "AOC renommée pour ses vins rouges et rosés",
    color: "#E74C3C",
    center: [43.15, 5.75],
    radius: 3000
  },
  {
    name: "Cassis",
    region: "Provence",
    description: "AOC côtière pour vins blancs et rosés",
    color: "#E67E22",
    center: [43.25, 5.65],
    radius: 2500
  },
  {
    name: "Palette",
    region: "Provence",
    description: "Petite AOC pour vins rouges, blancs et rosés",
    color: "#F39C12",
    center: [43.55, 5.45],
    radius: 2000
  },
  {
    name: "Côtes de Provence",
    region: "Provence",
    description: "Plus grande AOC de Provence",
    color: "#D35400",
    center: [43.35, 5.45],
    radius: 15000
  },
  {
    name: "Coteaux d'Aix-en-Provence",
    region: "Provence",
    description: "AOC pour vins rouges, blancs et rosés",
    color: "#E67E22",
    center: [43.5, 5.2],
    radius: 12000
  },
  {
    name: "Coteaux Varois en Provence",
    region: "Provence",
    description: "AOC pour vins rouges, blancs et rosés",
    color: "#F39C12",
    center: [43.6, 5.65],
    radius: 8000
  }
];

// Bordeaux AOCs with clean design - Complete list
const bordeauxAOCs = [
  // Médoc AOCs
  {
    name: "Médoc",
    region: "Bordeaux",
    description: "AOC pour vins rouges prestigieux",
    color: "#8B0000",
    center: [45.1, -0.8],
    radius: 8000
  },
  {
    name: "Haut-Médoc",
    region: "Bordeaux",
    description: "AOC pour vins rouges de qualité supérieure",
    color: "#A0522D",
    center: [45.05, -0.75],
    radius: 12000
  },
  {
    name: "Margaux",
    region: "Bordeaux",
    description: "AOC prestigieuse pour vins rouges élégants",
    color: "#8B4513",
    center: [45.05, -0.67],
    radius: 4000
  },
  {
    name: "Pauillac",
    region: "Bordeaux",
    description: "AOC pour vins rouges puissants et complexes",
    color: "#654321",
    center: [45.2, -0.75],
    radius: 3500
  },
  {
    name: "Saint-Julien",
    region: "Bordeaux",
    description: "AOC pour vins rouges équilibrés",
    color: "#8B0000",
    center: [45.15, -0.72],
    radius: 3000
  },
  {
    name: "Saint-Estèphe",
    region: "Bordeaux",
    description: "AOC pour vins rouges tanniques",
    color: "#A0522D",
    center: [45.25, -0.78],
    radius: 3500
  },
  {
    name: "Listrac-Médoc",
    region: "Bordeaux",
    description: "AOC pour vins rouges structurés",
    color: "#8B4513",
    center: [45.08, -0.8],
    radius: 2500
  },
  {
    name: "Moulis-en-Médoc",
    region: "Bordeaux",
    description: "AOC pour vins rouges fruités",
    color: "#654321",
    center: [45.06, -0.77],
    radius: 2000
  },

  // Graves AOCs
  {
    name: "Graves",
    region: "Bordeaux",
    description: "AOC pour vins rouges et blancs",
    color: "#D2691E",
    center: [44.8, -0.6],
    radius: 6000
  },
  {
    name: "Pessac-Léognan",
    region: "Bordeaux",
    description: "AOC pour vins rouges et blancs de qualité",
    color: "#CD853F",
    center: [44.75, -0.65],
    radius: 4500
  },

  // Libournais AOCs
  {
    name: "Saint-Émilion",
    region: "Bordeaux",
    description: "AOC pour vins rouges de qualité",
    color: "#8B4513",
    center: [44.9, -0.1],
    radius: 5000
  },
  {
    name: "Saint-Émilion Grand Cru",
    region: "Bordeaux",
    description: "AOC pour vins rouges de qualité supérieure",
    color: "#654321",
    center: [44.9, -0.1],
    radius: 5000
  },
  {
    name: "Pomerol",
    region: "Bordeaux",
    description: "AOC pour vins rouges prestigieux",
    color: "#8B0000",
    center: [45.0, -0.15],
    radius: 3000
  },
  {
    name: "Lalande-de-Pomerol",
    region: "Bordeaux",
    description: "AOC pour vins rouges de qualité",
    color: "#A0522D",
    center: [44.95, -0.18],
    radius: 2500
  },
  {
    name: "Fronsac",
    region: "Bordeaux",
    description: "AOC pour vins rouges structurés",
    color: "#8B4513",
    center: [44.92, -0.25],
    radius: 2000
  },
  {
    name: "Canon-Fronsac",
    region: "Bordeaux",
    description: "AOC pour vins rouges de qualité",
    color: "#654321",
    center: [44.9, -0.22],
    radius: 1500
  },

  // Entre-deux-Mers AOCs
  {
    name: "Entre-deux-Mers",
    region: "Bordeaux",
    description: "AOC pour vins blancs secs",
    color: "#32CD32",
    center: [44.7, -0.3],
    radius: 15000
  },
  {
    name: "Bordeaux",
    region: "Bordeaux",
    description: "AOC générique pour vins rouges et blancs",
    color: "#228B22",
    center: [44.8, -0.6],
    radius: 20000
  },
  {
    name: "Bordeaux Supérieur",
    region: "Bordeaux",
    description: "AOC pour vins rouges de qualité supérieure",
    color: "#006400",
    center: [44.8, -0.6],
    radius: 20000
  },

  // Côtes de Bordeaux AOCs
  {
    name: "Côtes de Bourg",
    region: "Bordeaux",
    description: "AOC pour vins rouges et blancs",
    color: "#8FBC8F",
    center: [45.05, -0.55],
    radius: 4000
  },
  {
    name: "Côtes de Blaye",
    region: "Bordeaux",
    description: "AOC pour vins rouges et blancs",
    color: "#90EE90",
    center: [45.1, -0.65],
    radius: 5000
  },
  {
    name: "Premières Côtes de Blaye",
    region: "Bordeaux",
    description: "AOC pour vins rouges de qualité",
    color: "#98FB98",
    center: [45.08, -0.62],
    radius: 3000
  },
  {
    name: "Côtes de Castillon",
    region: "Bordeaux",
    description: "AOC pour vins rouges de qualité",
    color: "#7CFC00",
    center: [44.85, -0.05],
    radius: 3500
  },
  {
    name: "Francs Côtes de Bordeaux",
    region: "Bordeaux",
    description: "AOC pour vins rouges de qualité",
    color: "#ADFF2F",
    center: [44.9, 0.05],
    radius: 2000
  },

  // Sweet Wine AOCs
  {
    name: "Sauternes",
    region: "Bordeaux",
    description: "AOC pour vins blancs liquoreux prestigieux",
    color: "#FFD700",
    center: [44.55, -0.35],
    radius: 3000
  },
  {
    name: "Barsac",
    region: "Bordeaux",
    description: "AOC pour vins blancs liquoreux",
    color: "#FFA500",
    center: [44.6, -0.3],
    radius: 2000
  },
  {
    name: "Cadillac",
    region: "Bordeaux",
    description: "AOC pour vins blancs liquoreux",
    color: "#FF8C00",
    center: [44.65, -0.25],
    radius: 2500
  },
  {
    name: "Loupiac",
    region: "Bordeaux",
    description: "AOC pour vins blancs liquoreux",
    color: "#FF7F50",
    center: [44.7, -0.2],
    radius: 2000
  },
  {
    name: "Sainte-Croix-du-Mont",
    region: "Bordeaux",
    description: "AOC pour vins blancs liquoreux",
    color: "#FF6347",
    center: [44.6, -0.25],
    radius: 1500
  }
];

// Argentina wine regions
const argentinaWineRegions = {
  type: "FeatureCollection",
  features: [
    {
      type: "Feature",
      properties: {
        name: "Mendoza",
        region: "Mendoza",
        description: "Région viticole principale d'Argentine",
        color: "#8B0000",
        type: "region"
      },
      geometry: {
        type: "Polygon",
        coordinates: [[
          [-69.5, -33.0], [-68.5, -33.0], [-68.5, -32.0], [-69.5, -32.0], [-69.5, -33.0]
        ]]
      }
    },
    {
      type: "Feature",
      properties: {
        name: "Salta",
        region: "Salta",
        description: "Région viticole du nord-ouest argentin",
        color: "#4B0082",
        type: "region"
      },
      geometry: {
        type: "Polygon",
        coordinates: [[
          [-66.0, -25.0], [-65.0, -25.0], [-65.0, -24.0], [-66.0, -24.0], [-66.0, -25.0]
        ]]
      }
    }
  ]
};

// Spain wine regions
const spainWineRegions = {
  type: "FeatureCollection",
  features: [
    {
      type: "Feature",
      properties: {
        name: "Rioja",
        region: "Rioja",
        description: "Région viticole célèbre d'Espagne",
        color: "#8B0000",
        type: "region"
      },
      geometry: {
        type: "Polygon",
        coordinates: [[
          [-2.8, 42.2], [-2.2, 42.2], [-2.2, 42.8], [-2.8, 42.8], [-2.8, 42.2]
        ]]
      }
    },
    {
      type: "Feature",
      properties: {
        name: "Ribera del Duero",
        region: "Ribera del Duero",
        description: "Région viticole de Castille-et-León",
        color: "#4B0082",
        type: "region"
      },
      geometry: {
        type: "Polygon",
        coordinates: [[
          [-4.2, 41.5], [-3.8, 41.5], [-3.8, 42.0], [-4.2, 42.0], [-4.2, 41.5]
        ]]
      }
    }
  ]
};

// Region-specific data and zoom levels
const regionData = {
  'Provence': {
    aocs: provenceAOCs,
    center: [43.6, 5.5],
    zoom: 9,
    bounds: [[43.1, 5.1], [43.7, 5.8]]
  },
  'Bordeaux': {
    aocs: bordeauxAOCs,
    center: [44.8, -0.6],
    zoom: 9,
    bounds: [[44.7, -0.9], [45.2, 0.0]]
  }
};

// Geographic coordinates for regions (latitude, longitude) - REMOVED: Now using centralized coordinates system

const countryRegions = {
  'France': franceWineRegions,
  'Argentine': argentinaWineRegions,
  'Espagne': spainWineRegions,
  'Italie': null,
  'Allemagne': null,
  'Mexique': null,
  'Suisse': null,
  'Autriche': null,
  'Australie': null,
  'Chili': null,
  'Grèce': null,
  'Portugal': null,
  'Nouvelle-Zélande': null
};

const countryCenters = {
  'France': [46.603354, 1.888334],
  'Argentine': [-32.5, -68.0],
  'Espagne': [40.463667, -3.74922],
  'Italie': [42.0, 12.0],
  'Allemagne': [50.0, 10.0],
  'Mexique': [23.0, -102.0],
  'Suisse': [46.8, 8.0],
  'Autriche': [47.5, 14.0],
  'Australie': [-25.0, 135.0],
  'Chili': [-35.0, -71.0],
  'Grèce': [39.0, 22.0],
  'Portugal': [39.5, -8.0],
  'Nouvelle-Zélande': [-42.0, 173.0]
};

const InteractiveWineMap = ({ 
  country = 'France', 
  mapAreas = [],
  highlightedRegions = [], 
  onRegionClick = null,
  style = {},
  height = 500,
  regionName = null,
  subregions = null
}) => {
  const [hoveredRegion, setHoveredRegion] = useState(null);
  const [hoveredLegend, setHoveredLegend] = useState(null);
  
  // Create dynamic GeoJSON from mapAreas if available
  const createGeoJSONFromMapAreas = (areas) => {
    if (!areas || areas.length === 0) return null;
    
    return {
      type: "FeatureCollection",
      features: areas.map(area => ({
        type: "Feature",
        properties: {
          name: area.region,
          region: area.region,
          description: `Région viticole de ${area.region}`,
          color: area.color,
          type: "region"
        },
        geometry: {
          type: "Point",
          coordinates: [area.x, area.y]
        }
      }))
    };
  };
  
  // Use mapAreas if available, otherwise fall back to hardcoded data
  const dynamicRegionsData = createGeoJSONFromMapAreas(mapAreas);
  const regionsData = dynamicRegionsData || countryRegions[country] || franceWineRegions;
  const center = countryCenters[country] || countryCenters['France'];
  
  // Determine if we're showing a specific region
  const isRegionView = regionName && (regionData[regionName] || (mapAreas.length > 0 && subregions));
  const regionInfo = isRegionView ? regionData[regionName] : null;
  
  // Calculate region center and zoom from mapAreas if not in hardcoded data
  const getRegionCenterAndZoom = () => {
    if (regionInfo) {
      return { center: regionInfo.center, zoom: regionInfo.zoom };
    }
    
    if (regionName) {
      // Use centralized coordinates system
      const coords = getRegionCoordinates(regionName);
      if (coords.lat && coords.lng) {
        return { 
          center: [coords.lat, coords.lng], 
          zoom: 9 // Zoom in more for region view
        };
      }
    }
    
    if (mapAreas.length > 0 && regionName) {
      // Fallback: Use the first mapArea as center and zoom in more for region view
      const firstArea = mapAreas[0];
      return { 
        center: [firstArea.y, firstArea.x], 
        zoom: 9 // Zoom in more for region view
      };
    }
    
    return { center, zoom: (country === 'France' ? 6 : 5) };
  };
  
  const { center: mapCenter, zoom: mapZoom } = getRegionCenterAndZoom();
  
  // Extract region names from mapAreas for highlighting
  const activeRegions = mapAreas.map(area => area.region);
  const regionsToHighlight = highlightedRegions.length > 0 ? highlightedRegions : activeRegions;

  const onEachFeature = (feature, layer) => {
    const regionName = feature.properties.name;
    const isHighlighted = regionsToHighlight.includes(regionName);
    const isActive = activeRegions.includes(regionName);
    const isLegendHovered = hoveredLegend === regionName;
    
    // In region view, show all AOCs; in country view, only show active regions
    if (!isRegionView && !isActive) {
      layer.setStyle({ opacity: 0 });
      return;
    }
    
    // Style the region
    layer.setStyle({
      fillColor: isHighlighted || isLegendHovered ? '#4B164C' : feature.properties.color,
      weight: isHighlighted || isLegendHovered ? 3 : 1,
      opacity: 1,
      color: isHighlighted || isLegendHovered ? '#4B164C' : '#999',
      fillOpacity: isHighlighted || isLegendHovered ? 0.8 : 0.3
    });

    // Hover effects
    layer.on({
      mouseover: (e) => {
        const layer = e.target;
        layer.setStyle({
          fillOpacity: 0.8,
          weight: 3
        });
        setHoveredRegion(regionName);
      },
      mouseout: (e) => {
        const layer = e.target;
        layer.setStyle({
          fillColor: isHighlighted || isLegendHovered ? '#4B164C' : feature.properties.color,
          fillOpacity: isHighlighted || isLegendHovered ? 0.8 : 0.3,
          weight: isHighlighted || isLegendHovered ? 3 : 1
        });
        setHoveredRegion(null);
      },
      click: () => {
        if (onRegionClick) {
          onRegionClick(regionName);
        }
      }
    });

    // Add popup
    layer.bindPopup(`
      <div style="text-align: center; min-width: 150px;">
        <h3 style="margin: 0 0 8px 0; color: #4B164C; font-size: 16px;">${regionName}</h3>
        <p style="margin: 0; color: #666; font-size: 14px;">${feature.properties.description}</p>
      </div>
    `);
  };

  // Create legend items for subregions if in region view
  const createSubregionLegendItems = () => {
    if (!subregions) return [];
    
    return Object.entries(subregions).map(([key, subregion]) => ({
      name: subregion.name,
      color: '#4B164C',
      description: subregion.description || `Appellation de ${subregion.name}`
    }));
  };

  // Legend data - use subregions if in region view, otherwise use existing logic
  const legendItems = isRegionView && subregions ? createSubregionLegendItems() :
    (isRegionView ? (regionInfo?.aocs || []) : 
    (dynamicRegionsData ? dynamicRegionsData.features : regionsData.features.filter(f => activeRegions.includes(f.properties.name))));

  return (
    <div style={{ 
      position: 'relative', 
      background: '#fff', 
      borderRadius: '12px', 
      padding: '24px', 
      boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
      border: '1px solid #f0f0f0',
      ...style 
    }}>
      <h2 style={{ 
        fontSize: '20px', 
        color: '#2c3e50', 
        margin: '0 0 20px 0', 
        fontWeight: '600',
        textAlign: 'center'
      }}>
        {isRegionView ? `AOCs de ${regionName}` : `Régions viticoles - ${country}`}
      </h2>
      
      <div style={{ 
        display: 'flex',
        gap: '20px',
        height: `${height}px`
      }}>
        {/* Map Container */}
        <div style={{ 
          flex: 1,
          borderRadius: '8px', 
          overflow: 'hidden',
          border: '1px solid #e8e8e8',
          position: 'relative'
        }}>
          <MapContainer
            center={mapCenter}
            zoom={mapZoom}
            style={{ height: '100%', width: '100%' }}
            zoomControl={false}
            bounds={isRegionView && regionInfo?.bounds ? regionInfo.bounds : undefined}
            attributionControl={false}
          >
            <TileLayer
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
            />
            
            {/* Show regions for country view */}
            {!isRegionView && (
              <>
                {/* Show Polygon regions from hardcoded data */}
                {!dynamicRegionsData && (
                  <GeoJSON
                    data={regionsData}
                    onEachFeature={onEachFeature}
                    style={{
                      fillColor: '#4B164C',
                      weight: 1,
                      opacity: 1,
                      color: '#999',
                      fillOpacity: 0.3
                    }}
                  />
                )}
                
                {/* Show Point regions from mapAreas */}
                {dynamicRegionsData && mapAreas.map((area, index) => {
                  const isHighlighted = regionsToHighlight.includes(area.region);
                  const isActive = activeRegions.includes(area.region);
                  const isLegendHovered = hoveredLegend === area.region;
                  
                  return (
                    <CircleMarker
                      key={area.region}
                      center={[area.y, area.x]}
                      radius={area.size || 8}
                      pathOptions={{
                        fillColor: isHighlighted || isLegendHovered ? '#4B164C' : area.color,
                        color: isHighlighted || isLegendHovered ? '#4B164C' : '#999',
                        weight: isHighlighted || isLegendHovered ? 3 : 1,
                        fillOpacity: isHighlighted || isLegendHovered ? 0.8 : 0.6
                      }}
                      eventHandlers={{
                        mouseover: () => setHoveredRegion(area.region),
                        mouseout: () => setHoveredRegion(null),
                        click: () => {
                          if (onRegionClick) {
                            onRegionClick(area.region);
                          }
                        }
                      }}
                    >
                      <Popup>
                        <div style={{textAlign: 'center', minWidth: '150px'}}>
                          <h3 style={{margin: '0 0 8px 0', color: '#4B164C', fontSize: '16px'}}>{area.region}</h3>
                          <p style={{margin: '0', color: '#666', fontSize: '14px'}}>Région viticole de {area.region}</p>
                        </div>
                      </Popup>
                    </CircleMarker>
                  );
                })}
              </>
            )}
            
            {/* Show AOC circles for region view */}
            {isRegionView && regionInfo?.aocs?.map((aoc, index) => {
              const isLegendHovered = hoveredLegend === aoc.name;
              return (
                <Circle
                  key={aoc.name}
                  center={aoc.center}
                  radius={aoc.radius}
                  pathOptions={{
                    fillColor: isLegendHovered ? '#4B164C' : aoc.color,
                    color: isLegendHovered ? '#4B164C' : '#999',
                    weight: isLegendHovered ? 3 : 1,
                    fillOpacity: isLegendHovered ? 0.8 : 0.4
                  }}
                  eventHandlers={{
                    mouseover: () => setHoveredRegion(aoc.name),
                    mouseout: () => setHoveredRegion(null),
                    click: () => {
                      if (onRegionClick) {
                        onRegionClick(aoc.name);
                      }
                    }
                  }}
                >
                  <Popup>
                    <div style={{textAlign: 'center', minWidth: '150px'}}>
                      <h3 style={{margin: '0 0 8px 0', color: '#4B164C', fontSize: '16px'}}>{aoc.name}</h3>
                      <p style={{margin: '0', color: '#666', fontSize: '14px'}}>{aoc.description}</p>
                    </div>
                  </Popup>
                </Circle>
              );
            })}
            
            {/* Show subregion markers for dynamic region view */}
            {isRegionView && subregions && mapAreas.length > 0 && (
              <>
                {/* Show the main region marker */}
                {mapAreas.map((area, index) => {
                  const isLegendHovered = hoveredLegend === area.region;
                  // Use centralized coordinates system
                  const coords = getRegionCoordinates(area.region);
                  const markerCenter = [coords.lat, coords.lng];
                  
                  return (
                    <CircleMarker
                      key={area.region}
                      center={markerCenter}
                      radius={12}
                      pathOptions={{
                        fillColor: isLegendHovered ? '#4B164C' : area.color,
                        color: isLegendHovered ? '#4B164C' : '#999',
                        weight: isLegendHovered ? 3 : 2,
                        fillOpacity: isLegendHovered ? 0.8 : 0.6
                      }}
                      eventHandlers={{
                        mouseover: () => setHoveredRegion(area.region),
                        mouseout: () => setHoveredRegion(null),
                        click: () => {
                          if (onRegionClick) {
                            onRegionClick(area.region);
                          }
                        }
                      }}
                    >
                      <Popup>
                        <div style={{textAlign: 'center', minWidth: '150px'}}>
                          <h3 style={{margin: '0 0 8px 0', color: '#4B164C', fontSize: '16px'}}>{area.region}</h3>
                          <p style={{margin: '0', color: '#666', fontSize: '14px'}}>Région viticole de {area.region}</p>
                        </div>
                      </Popup>
                    </CircleMarker>
                  );
                })}
                
                {/* Show subregion markers around the main region */}
                {Object.entries(subregions).map(([key, subregion], index) => {
                  const isLegendHovered = hoveredLegend === subregion.name;
                  // Calculate position around the main region center using centralized coordinates
                  const mainCoords = getRegionCoordinates(regionName);
                  const mainCenter = [mainCoords.lat, mainCoords.lng];
                  const angle = (index * 72) * (Math.PI / 180); // Distribute in a circle
                  const distance = 0.2; // Distance from center in degrees
                  const subregionCenter = [
                    mainCenter[0] + Math.cos(angle) * distance,
                    mainCenter[1] + Math.sin(angle) * distance
                  ];
                  
                  return (
                    <CircleMarker
                      key={key}
                      center={subregionCenter}
                      radius={8}
                      pathOptions={{
                        fillColor: isLegendHovered ? '#4B164C' : '#6d2c7a',
                        color: isLegendHovered ? '#4B164C' : '#999',
                        weight: isLegendHovered ? 3 : 1,
                        fillOpacity: isLegendHovered ? 0.8 : 0.6
                      }}
                      eventHandlers={{
                        mouseover: () => setHoveredRegion(subregion.name),
                        mouseout: () => setHoveredRegion(null),
                        click: () => {
                          if (onRegionClick) {
                            onRegionClick(subregion.name);
                          }
                        }
                      }}
                    >
                      <Popup>
                        <div style={{textAlign: 'center', minWidth: '150px'}}>
                          <h3 style={{margin: '0 0 8px 0', color: '#4B164C', fontSize: '16px'}}>{subregion.name}</h3>
                          <p style={{margin: '0', color: '#666', fontSize: '14px'}}>{subregion.description}</p>
                          <p style={{margin: '4px 0 0 0', color: '#888', fontSize: '12px'}}>Surface: {subregion.area}</p>
                        </div>
                      </Popup>
                    </CircleMarker>
                  );
                })}
              </>
            )}
            
            <ZoomControl position="topright" />
          </MapContainer>
        </div>

        {/* Legend */}
        <div style={{
          width: '200px',
          background: '#fafafa',
          borderRadius: '8px',
          padding: '16px',
          border: '1px solid #e8e8e8',
          height: 'fit-content',
          maxHeight: '100%',
          overflowY: 'auto'
        }}>
          <h3 style={{
            fontSize: '14px',
            color: '#2c3e50',
            margin: '0 0 12px 0',
            fontWeight: '600',
            textAlign: 'center'
          }}>
            {isRegionView ? 'Appellations' : 'Régions'}
          </h3>
          
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {legendItems.map((item, index) => {
              const name = isRegionView ? item.name : item.properties.name;
              const color = isRegionView ? item.color : item.properties.color;
              const isHovered = hoveredRegion === name || hoveredLegend === name;
              
              return (
                <div
                  key={name}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '8px',
                    borderRadius: '6px',
                    background: isHovered ? '#4B164C' : 'transparent',
                    color: isHovered ? '#fff' : '#2c3e50',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    fontSize: '13px',
                    fontWeight: isHovered ? '600' : '500'
                  }}
                  onMouseEnter={() => setHoveredLegend(name)}
                  onMouseLeave={() => setHoveredLegend(null)}
                  onClick={() => {
                    if (onRegionClick) {
                      onRegionClick(name);
                    }
                  }}
                >
                  <div style={{
                    width: '12px',
                    height: '12px',
                    borderRadius: '50%',
                    background: color,
                    border: '1px solid #ddd',
                    flexShrink: 0
                  }} />
                  <span style={{ 
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis'
                  }}>
                    {name}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      </div>
      
      {/* Hover indicator */}
      {hoveredRegion && (
        <div style={{
          position: 'absolute',
          top: '16px',
          right: '16px',
          background: 'rgba(75,22,76,0.95)',
          color: '#fff',
          padding: '8px 12px',
          borderRadius: '6px',
          fontSize: '13px',
          fontWeight: '500',
          zIndex: 1000,
          boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
          maxWidth: '200px',
          wordWrap: 'break-word'
        }}>
          {isRegionView ? 'Appellation' : 'Région'}: {hoveredRegion}
        </div>
      )}
    </div>
  );
};

export default InteractiveWineMap; 